<template>
    <div class="app-v2 flex min-h-svh w-full flex-col bg-surface-page dark:bg-background">
        <Intercom />

        <div
            v-if="globalProps.user.isImpersonating"
            class="fixed bottom-0 left-0 flex flex-wrap items-center justify-between w-full px-10 py-2 z-500 bg-new-neutral-900/50"
        >
            <div class="my-1 text-sm text-white">
                You are now seeing the app as: {{ globalProps.user.name }}
            </div>
            <Button size="sm" variant="secondary" @click="endImpersonation">
                <EyeOff class="size-4" />
                Leave impersonation
            </Button>
        </div>

        <ConfirmDialog></ConfirmDialog>
        <Toaster position="bottom-center" />

        <ShoppingCartModal />

        <!-- Sticky header: topbar + banners pin together as one unit -->
        <div class="sticky top-0 z-30 bg-background">
            <TopbarV2 />

            <BannerStackV2 />

        <!-- Last in the stack, so a real warning always sits above it. -->
        <LettrPromoBannerV2 v-if="shouldDisplayLettrPromoBanner" />
        </div>

        <!-- Main content -->
        <div class="mx-3 mb-3 flex flex-col flex-1 gradient-borders">
            <div class="flex flex-col flex-1 gradient-background">
                <main class="flex-1">
                    <slot />
                </main>
            </div>
        </div>
    </div>
</template>
<script lang="ts" setup>
import { computed, onMounted, onUnmounted } from 'vue';
import ConfirmDialog from '@atomic/Dialogs/ConfirmDialog.vue';
import { usePage } from '@inertiajs/vue3';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import BannerStackV2 from '../components/Navigation/components/v2/BannerStackV2.vue';
import LettrPromoBannerV2 from '../components/Navigation/components/v2/LettrPromoBannerV2.vue';
import useLettrPromo from '@/composables/useLettrPromo';
import { Button } from '@/components/ui/button';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { EyeOff } from 'lucide-vue-next';
import Bugsnag from '@bugsnag/js';
import ICurrentTeam from '@/types/Members/ICurrentTeam';
import TopbarV2 from '@/components/Navigation/TopbarV2.vue';
import { Toaster } from '@/components/ui/sonner';
import 'vue-sonner/style.css';

const globalProps = computed(() => usePage<ITopolPageProps>().props);
const teamProps = computed(
    (): ICurrentTeam | null =>
        (globalProps.value.team as ICurrentTeam | undefined) ?? null
);

const { shouldDisplayLettrPromoBanner } = useLettrPromo();

let removeNavigateListener: (() => void) | null = null;

onMounted(async () => {
    // Marker for v2-only global style overrides (e.g. Inertia/NProgress bar color).
    document.body.classList.add('app-v2');

    const userObject = {
        id: globalProps.value.user?.id,
        email: globalProps.value.user?.email,
        current_team_id: teamProps.value?.id ?? null,
    };
    Bugsnag.setUser(JSON.stringify(userObject));

    await getData();

    let lastSeenTeamId = teamProps.value?.id;
    removeNavigateListener = router.on('navigate', () => {
        const currentTeamId = teamProps.value?.id;
        if (currentTeamId !== lastSeenTeamId) {
            lastSeenTeamId = currentTeamId;
            getCurrentTeamMembersCount();
        }
    });
});

onUnmounted(() => {
    document.body.classList.remove('app-v2');
    removeNavigateListener?.();
});

const { fetchBanners } = useBanners();

const { fetchSubscriptions } = useSubscriptions();

const { fetchTeams, getCurrentTeamMembersCount } = useTeamsAndMembers();

const getData = async () => {
    await fetchSubscriptions();
    await fetchBanners();
    await getCurrentTeamMembersCount();
    await fetchTeams();
};

const endImpersonation = () => {
    router.delete(
        route('kiosk.users.nonimpersonate', {
            user: globalProps.value.user.id,
        }),
        {
            onSuccess: () => {
                getData();
            },
        }
    );
};
</script>
